/**
 * Helpers for injecting a per-business brand color into the shadcn theme.
 *
 * Shadcn stores its theme tokens as raw HSL components (e.g. `0 0% 9%`),
 * NOT wrapped in `hsl()`, and Tailwind classes resolve `bg-primary` to
 * `background-color: hsl(var(--primary))`. So if we override `--primary`
 * with a hex value like `#a28007`, the `hsl(#a28007)` call is invalid and
 * Tailwind silently falls back to nothing — the result is invisible
 * buttons / tabs / borders.
 *
 * `brandedThemeStyle(hex)` returns a React inline-style object with the
 * matching HSL components for `--primary` and a contrasting
 * `--primary-foreground`. Spread it on the outermost wrapper of a
 * branded surface (Auth, BusinessDashboard's access-denied card, etc).
 */

import type { CSSProperties } from "react";

interface RGB {
    r: number;
    g: number;
    b: number;
}

function parseHex(hex: string): RGB | null {
    const v = hex.replace(/^#/, "").trim();
    const expanded = v.length === 3
        ? v.split("").map((c) => c + c).join("")
        : v;
    if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;
    return {
        r: parseInt(expanded.slice(0, 2), 16),
        g: parseInt(expanded.slice(2, 4), 16),
        b: parseInt(expanded.slice(4, 6), 16),
    };
}

interface HSL {
    h: number;
    s: number;
    l: number;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case rn:
                h = (gn - bn) / d + (gn < bn ? 6 : 0);
                break;
            case gn:
                h = (bn - rn) / d + 2;
                break;
            case bn:
                h = (rn - gn) / d + 4;
                break;
        }
        h /= 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
}

/** Builds the shadcn HSL-triplet vars for one brand color under a given prefix:
 *  `{ "--<prefix>": "h s% l%", "--<prefix>-foreground": "0 0% 9%|98%",
 *     "--<prefix>-hex": "#rrggbb" }`. The raw hex passthrough is for gradients /
 *  `color-mix()` where the HSL-triplet form is awkward. Returns `{}` for invalid
 *  input so callers can spread unconditionally. */
function hslVarsFor(hex: string | null | undefined, prefix: string): Record<string, string> {
    if (!hex) return {};
    const rgb = parseHex(hex);
    if (!rgb) return {};
    const { h, s, l } = rgbToHsl(rgb);
    // Relative luminance via the standard sRGB coefficients — picks a foreground
    // that won't disappear into the brand color background.
    const relLuma = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    const foreground = relLuma > 0.6 ? "0 0% 9%" : "0 0% 98%";
    return {
        [`--${prefix}`]: `${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%`,
        [`--${prefix}-foreground`]: foreground,
        [`--${prefix}-hex`]: hex.startsWith("#") ? hex : `#${hex}`,
    };
}

/** Picks a readable foreground ("#ffffff" or "#111111") for text/icons placed
 *  on top of a solid brand color. Falls back to white for invalid input. Mirrors
 *  the relative-luminance approach used for the shadcn `-foreground` vars. */
export function readableOn(hex: string | null | undefined): "#ffffff" | "#111111" {
    const rgb = hex ? parseHex(hex) : null;
    if (!rgb) return "#ffffff";
    const relLuma = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return relLuma > 0.6 ? "#111111" : "#ffffff";
}

/** Returns a React inline-style object that overrides shadcn's `--primary`
 *  and `--primary-foreground` CSS variables with the business's brand
 *  color. Returns `undefined` for invalid input so callers can spread
 *  unconditionally. */
export function brandedThemeStyle(hex: string | null | undefined): CSSProperties | undefined {
    const vars = hslVarsFor(hex, "primary");
    return Object.keys(vars).length ? (vars as CSSProperties) : undefined;
}

/** Full-brand theme for the customer portal: overrides `--primary`, `--secondary`
 *  and `--accent` (+ their `-foreground` and `-hex` passthrough vars) so Tailwind
 *  `bg-primary/secondary/accent` + gradients resolve to the business's brand.
 *  Always returns an object (possibly empty) so it can be spread unconditionally. */
export function brandedPortalTheme(colors: {
    primary?: string | null;
    secondary?: string | null;
    accent?: string | null;
}): CSSProperties {
    return {
        ...hslVarsFor(colors.primary, "primary"),
        ...hslVarsFor(colors.secondary, "secondary"),
        ...hslVarsFor(colors.accent, "accent"),
    } as CSSProperties;
}
